import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, Globe, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import SyllabusLeadModal from "@/components/SyllabusLeadModal";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BADGE_STYLES = {
  "Featured": "bg-[#FFC72C] text-[#17263B]",
  "Popular": "bg-orange-500/95 text-white",
  "Trending": "bg-red-500/95 text-white",
  "New": "bg-emerald-500/95 text-white",
  "Learner's Choice": "bg-violet-600/95 text-white",
  "In Demand": "bg-rose-500/95 text-white",
};

export function feeLabel(p) {
  if (p.free || !p.tuition_fee) return "Free";
  const sym = p.currency === "INR" ? "₹" : `${p.currency} `;
  return `${sym}${Number(p.tuition_fee).toLocaleString("en-IN")}`;
}

export default function CourseCard({ program: p }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leadOpen, setLeadOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const initials = (p.partner || p.university || "TB").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const syllabus = p.syllabus_url || p.brochure_url;
  const provider = p.partner || p.university || "Training Basket";
  const duration = p.duration_term || (p.duration_months ? `${p.duration_months} Months` : "");
  const primaryLabel = p.cta_primary_label || "View Details";
  const applyLabel = p.cta_secondary_label || (p.syllabus_url ? "Syllabus" : p.brochure_url ? "Brochure" : "Apply Now");
  const f = p.filters || {};
  const pills = (f["Program Highlights"] || f["program highlights"] || p.skills || []).slice(0, 3);
  const learnMode = f["Learning Mode"] || f["learning mode"] || (Array.isArray(f.mode) ? f.mode.join(" / ") : null) || p.mode || "Online";

  const goDetails = () => {
    if (p.cta_primary_link) { window.open(p.cta_primary_link, "_blank", "noopener"); return; }
    navigate(`/courses/${p.slug}`);
  };

  const onApply = async () => {
    if (p.cta_secondary_link) { window.open(p.cta_secondary_link, "_blank", "noopener"); return; }
    if (syllabus) { setLeadOpen(true); return; }
    if (!user) { navigate(`/signup?next=${encodeURIComponent(`/courses/${p.slug}`)}&apply=1`); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/applications", { program_slug: p.slug });
      navigate(`/apply/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col cursor-pointer h-full"
      onClick={goDetails} role="link" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) goDetails(); }}
      data-testid={`course-card-${p.slug}`}>
      <div className="relative">
        <div className="aspect-[8/3] overflow-hidden rounded-xl mx-3 mt-3 bg-[#FFF9E6]" data-testid={`card-cover-wrap-${p.slug}`}>
          {p.image ? (
            <img src={p.image} alt={p.title} className="w-full h-full object-contain p-2" loading="lazy" data-testid={`card-cover-${p.slug}`} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#17263B] to-[#1E3A5F] flex items-center justify-center">
              <span className="text-4xl font-extrabold text-white/15">{initials}</span>
            </div>
          )}
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {p.featured && <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${BADGE_STYLES.Featured}`}>Featured</span>}
            {(p.showcase || []).slice(0, 2).map((s) => (
              <span key={s} className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${BADGE_STYLES[s] || "bg-amber-400/95 text-[#17263B]"}`} data-testid={`card-showcase-${p.slug}`}>{s}</span>
            ))}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-extrabold backdrop-blur ${p.workspace === "college" ? "bg-indigo-600/95 text-white" : "bg-white/90 text-slate-700"}`} data-testid={`card-type-${p.slug}`}>{p.workspace === "college" ? "Degree / University" : "Certification"}</span>
          </div>
        </div>
        <div className="absolute -bottom-6 right-7 w-14 h-14 rounded-xl bg-white border border-slate-200 shadow-md flex items-center justify-center overflow-hidden" data-testid={`card-logo-wrap-${p.slug}`}>
          {p.logo ? (
            <img src={p.logo} alt={provider} className="w-full h-full object-contain p-1.5" data-testid={`card-logo-${p.slug}`} />
          ) : (
            <span className="text-xs font-extrabold text-[#17263B]">{initials}</span>
          )}
        </div>
      </div>

      <div className="p-5 pt-8 flex flex-col flex-1">
        <p className="text-xs font-semibold text-slate-400" data-testid={`card-provider-${p.slug}`}>{provider}</p>
        <h3 className="font-extrabold text-slate-900 text-lg mt-1 leading-snug line-clamp-2 min-h-[3.2rem]">{p.title}</h3>
        {pills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2.5" data-testid={`card-pills-${p.slug}`}>
            {pills.map((s) => <span key={s} className="px-3 py-1.5 rounded-lg bg-[#EDF1F7] text-[#334155] text-xs font-semibold">{s}</span>)}
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
          {duration && (
            <span className="flex items-center gap-2 text-sm text-slate-600" data-testid={`card-duration-${p.slug}`}>
              <span className="w-7 h-7 rounded-full bg-[#FFC72C]/20 flex items-center justify-center shrink-0"><Clock className="w-3.5 h-3.5 text-[#9A7B0A]" /></span>
              {duration}
            </span>
          )}
          <span className="flex items-center gap-2 text-sm text-slate-600" data-testid={`card-mode-${p.slug}`}>
            <span className="w-7 h-7 rounded-full bg-[#FFC72C]/20 flex items-center justify-center shrink-0"><Globe className="w-3.5 h-3.5 text-[#9A7B0A]" /></span>
            {learnMode}
          </span>
        </div>

        <div className="flex items-center gap-2.5 mt-auto pt-4">
          {p.cta_primary_link ? (
            <a href={p.cta_primary_link} target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button variant="outline" className="w-full rounded-full font-bold border-slate-300" data-testid={`view-course-${p.slug}`}>{primaryLabel}</Button>
            </a>
          ) : (
            <Link to={p.url_path || `/courses/${p.slug}`} className="flex-1">
              <Button variant="outline" className="w-full rounded-full font-bold border-slate-300" data-testid={`view-course-${p.slug}`}>{primaryLabel}</Button>
            </Link>
          )}
          <Button onClick={(e) => { e.stopPropagation(); onApply(); }} disabled={applying} className="flex-1 rounded-full font-bold bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B]" data-testid={`apply-course-${p.slug}`}>
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{applyLabel} <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
          <SyllabusLeadModal open={leadOpen} onOpenChange={setLeadOpen} program={p} downloadUrl={syllabus || ""} />
        </div>
      </div>
    </div>
  );
}
